import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { isDefined, numberOrZero, toDate } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteSpecialCommissionsRule } from "../../../../../common/api/queries";
import ActionTextIcon from "../../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../../common/components/views/Ellipsis";
import { PageSizes, TableSizes } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { formatAgentName } from "../../../../agent/utils";
import { SpecialCommissionsSettingsRuleType } from "../../enums";
import { SpecialCommissionsSettingsRule } from "../../types";
import { isRateRuleType } from "../../utils";
import SpecialCommissionsSettingsRuleSuspendedTag from "../SpecialCommissionsSettingsRuleSuspendedTag";

interface Props {
  rules: SpecialCommissionsSettingsRule[];
  agentId: UUID;
  showActions: boolean;
  onUpdateClick: (rule: SpecialCommissionsSettingsRule) => void;
}

export const SpecialCommissionsSettingsRuleTable = ({ rules, agentId, showActions, onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const searchParams = useFilterSearchParams();

  const deleteSpecialCommissionsRule = useDeleteSpecialCommissionsRule();

  const columns: ColumnsType<SpecialCommissionsSettingsRule> = [
    {
      key: "code",
      title: t("commissions.special.attrs.code"),
      width: 120,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.code}</Ellipsis>,
    },
    {
      key: "name",
      title: t("commissions.special.attrs.name"),
      width: 300,
      render: (_, record) => record.name,
    },
    {
      key: "type",
      title: t("commissions.special.enums.ruleType._label"),
      width: 150,
      render: (_, record) => t("commissions.special.enums.ruleType." + record.type),
    },
    {
      key: "startDate",
      title: t("commissions.special.attrs.startDate"),
      width: 120,
      render: (_, record) =>
        record.type === SpecialCommissionsSettingsRuleType.ONE_TIME
          ? (toDate(record.startDate) as Dayjs).format("MMMM YYYY")
          : record.startDate
            ? formatLocaleDate(record.startDate)
            : undefined,
    },
    {
      key: "endDate",
      title: t("commissions.special.attrs.endDate"),
      width: 120,
      render: (_, record) =>
        record.type === SpecialCommissionsSettingsRuleType.ONE_TIME
          ? (toDate(record.endDate) as Dayjs).format("MMMM YYYY")
          : record.endDate
            ? formatLocaleDate(record.endDate)
            : undefined,
    },
    {
      key: "suspended",
      width: 110,
      render: (_, record) => record.suspended && <SpecialCommissionsSettingsRuleSuspendedTag />,
    },
    {
      key: "payer",
      title: t("commissions.special.attrs.payer"),
      width: 200,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{formatAgentName(record.payer)}</Ellipsis>,
    },
    {
      key: "amountOrRate",
      title: t("commissions.special.attrs.commissionAmount"),
      width: 200,
      align: "right",
      render: (_, record) =>
        isRateRuleType(record.type) ? (
          <>
            {t(
              record.type === SpecialCommissionsSettingsRuleType.NETTO_POINTS_RATE
                ? "commissions.special.helpers.nettoPointsRuleDesc"
                : "commissions.special.helpers.commissionAmountRuleDesc",
              { rate: isDefined(record.rate) ? formatLocalePercentageNumber(record.rate) : undefined },
            )}
            <br />
            {formatAgentName(record.targetAgent)}
          </>
        ) : isDefined(record.amount) ? (
          formatLocaleCurrency(record.amount)
        ) : undefined,
    },
  ];

  if (showActions) {
    columns.push({
      key: "actions",
      width: 180,
      align: "right",
      fixed: "right",
      render: (_, record) => (
        <div className="nowrap">
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("commissions.special.helpers.deleteRuleConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => deleteSpecialCommissionsRule.mutate({ id1: agentId, id2: record.id })}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </div>
      ),
    });
  }

  return (
    <Table<SpecialCommissionsSettingsRule>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.HUGE }}
      dataSource={rules}
      pagination={{
        ...paginationTableProps,
        pageSize: PageSizes.MEDIUM,
        current: numberOrZero(searchParams.pageIndex) + 1,
        total: rules.length,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
